// Carousel — the craft.js content-block wrapper for the DL-041 Image Carousel
// (LOCKED film-strip deck spec). Mirrors the Image/Video/ImageGrid block contract
// exactly: a useNode() component that connect(drag)s its root, wraps the render in
// <BlockIndicator> (builder chrome), and exposes a static `.craft` with default
// props + related.settings + displayName. The SAME component renders in the
// builder AND the published Segment view (Segment view mounts craft with
// enabled=false) — we read `enabled` only to suppress auto-advance while authoring.
//
// All carousel behaviour/markup lives in the craft-free CarouselView; this wrapper
// is the thin craft/Redux adapter (margin, alignment, image normalisation).

import { useEditor, useNode } from "@craftjs/core";
import React from "react";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import CarouselView from "./CarouselView";
import { normalizeImages } from "./carouselLogic";
import CarouselSettings from "./CarouselSettings";

export const Carousel = ({
  images,
  mode,
  speed,
  fit,
  height,
  showCaptions,
  showArrows,
  showDots,
  borderRadius,
  hyperlink,
  showHyperlink,
  loop,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  alignment,
}) => {
  const {
    connectors: { connect, drag },
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const normalized = normalizeImages(images);

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: alignment || "center",
            padding: marginStyle,
            boxSizing: "border-box",
          }}
        >
          <div style={{ width: "100%", maxWidth: "100%" }}>
            <CarouselView
              images={normalized}
              mode={mode}
              speed={speed}
              fit={fit}
              height={height}
              showCaptions={showCaptions}
              showArrows={showArrows}
              showDots={showDots}
              borderRadius={borderRadius}
              hyperlink={hyperlink}
              showHyperlink={showHyperlink}
              loop={loop}
              editing={enabled}
            />
          </div>
        </div>
      </BlockIndicator>
    </div>
  );
};

export const CarouselDefaultProps = {
  images: [],
  shallowImages: [], // authoring-only parallel list the shared <List> reorder/remove uses
  mode: "manual", // deck default: manual arrows / dots / swipe
  speed: "medium", // deck default (used only in Auto mode)
  fit: "keep-shape", // deck LOCKED default: each image keeps its own shape
  height: 260,
  showCaptions: false,
  showArrows: true,
  showDots: true,
  borderRadius: 12,
  hyperlink: "",
  showHyperlink: false,
  loop: true,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  alignment: undefined,
};

Carousel.craft = {
  props: CarouselDefaultProps,
  related: {
    settings: CarouselSettings,
  },
  displayName: "CarouselWidget",
};

export default Carousel;
